import { Outlet, useMatch, useParams, useSearchParams } from "react-router-dom"

import { NAMES_ROUTES } from "@/const/routes.constants"
import { useTranslation } from "@/hooks/useTranslation"
import { useUser } from "@/hooks/useUser"
import {
  getLiveRuleGroups,
  withAccessRules
} from "@/pages/GovernancePage/applications/ai-policies/liveRuleGroups.selector"
import {
  AI_POLICIES_WRITE_PERMISSION,
  useAiPoliciesListParams,
  useAiPolicyAgentOptions,
  useAiPolicyConditionOptions,
  useListedAiPolicies,
  useLiveAiPolicies
} from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies"
import {
  AI_POLICY_KIND_PARAM,
  getAiPolicyKind,
  getAiPolicyRecords,
  getAiPolicyRequest
} from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies.selector"
import {
  AI_POLICY_FEATURES,
  AiPoliciesContext,
  PENDING_AI_POLICIES_CONTEXT
} from "@/pages/GovernancePage/applications/policies/useAiPolicies"

import type { AiPolicy } from "@/api/useAiPolicies.types"
import type { AiPoliciesContextValue } from "@/pages/GovernancePage/applications/policies/useAiPolicies.types"
import type { LiveAiPolicyProviderProps } from "./LiveAiPolicyProvider.types"

const demoOnly = (name: string) => () => {
  throw new Error(`${name} is a demo AI policies operation`)
}

const getPolicy = (record: { live?: AiPolicy }) => {
  if (!record.live) throw new Error("The record is not a real AI policy")

  return record.live
}

// The AI policies the API enforces, served to the same pages as the demo ones
export const LiveAiPolicyProvider = (props: LiveAiPolicyProviderProps) => {
  const { t } = useTranslation()
  const { hasPermission } = useUser()
  const params = useParams<{ id: string; mode?: "view" | "edit" }>()
  const { id, mode } = params
  const [searchParams] = useSearchParams()
  const policies = useLiveAiPolicies()
  const agents = useAiPolicyAgentOptions()
  const conditionOptions = useAiPolicyConditionOptions()
  const canWrite = !!hasPermission(AI_POLICIES_WRITE_PERMISSION)
  const isSingleView = !!id && (mode === "edit" || mode === "view")
  // The list's rows, as its filters and order ask; every policy stays in the
  // response, for the summary, enforcing all and finding one. Only the list
  // reads them: the decisions tab keeps its own filters and order in the url
  const isList = !!useMatch(
    `/${NAMES_ROUTES.GOVERNANCE.ROOT}/${NAMES_ROUTES.GOVERNANCE.AI}`
  )
  const listParams = useAiPoliciesListParams()
  const listed = useListedAiPolicies(isList ? listParams : undefined)
  const isPending = policies.isLoading || agents.isLoading || listed.isLoading
  // An existing policy keeps its kind; a new one has the kind it was created as
  const kind = getAiPolicyKind(
    isSingleView
      ? policies.policies.find((policy) => policy.id === id)
      : undefined,
    searchParams.get(AI_POLICY_KIND_PARAM)
  )

  const getValue = (): AiPoliciesContextValue => {
    const ruleOptions = getLiveRuleGroups(t, conditionOptions, kind)
    const common = {
      ...PENDING_AI_POLICIES_CONTEXT,
      source: "live" as const,
      features: AI_POLICY_FEATURES.live,
      ruleOptions,
      mode
    }

    if (isPending) return common

    const records = withAccessRules(
      getAiPolicyRecords(policies.policies, agents.options, t),
      t
    )
    const findById = (needle: string) =>
      records.find((item) => `${item.id}` === needle)
    const { create, update, remove, setEnabled } = policies

    return {
      ...common,
      isPending: false,
      response: {
        data: isSingleView
          ? records.filter((item) => `${item.id}` === id)
          : records
      },
      listed:
        listed.policies &&
        withAccessRules(
          getAiPolicyRecords(listed.policies, agents.options, t),
          t
        ),
      findById,
      update: demoOnly("update"),
      create: demoOnly("create"),
      saveAgentPolicy: demoOnly("saveAgentPolicy"),
      approveSuggestions: demoOnly("approveSuggestions"),
      live: {
        canWrite,
        isError: policies.isError,
        refetch: policies.refetch,
        agentOptions: agents.options,
        kind,
        save: (values, record) => {
          if (!record) return create(getAiPolicyRequest(values, true, kind))

          const policy = getPolicy(record)

          return update(
            policy.id,
            getAiPolicyRequest(values, policy.enabled, getAiPolicyKind(policy))
          )
        },
        remove: (record) => remove(getPolicy(record).id),
        setEnabled: (record, enabled) => setEnabled(getPolicy(record), enabled)
      }
    }
  }

  return (
    <AiPoliciesContext.Provider value={getValue()}>
      {props.children ?? <Outlet />}
    </AiPoliciesContext.Provider>
  )
}
